import { AwsEnvironmentReport } from '@/services/systemHealth';

export interface Finding {
    tone: 'bad' | 'warn' | 'info';
    text: string;
}

const CPU_HIGH = 80;
const clock = (d: Date) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
const UNHEALTHY = /transitioned from \w+ to (Warning|Degraded|Severe)/;

/** Plain-language findings, most serious first; only restates what EB and the events say. */
export function diagnose(report: AwsEnvironmentReport, now: number): Finding[] {
    const findings: Finding[] = [];
    const { service, instances, events } = report;

    for (const cause of service.causes) findings.push({ tone: service.health === 'down' ? 'bad' : 'warn', text: cause });

    for (const i of instances) {
        if (i.health !== 'ok' && i.causes.length)
            findings.push({ tone: i.health === 'down' ? 'bad' : 'warn', text: `${i.id}: ${i.causes.join(' ')}` });
        if (i.deploymentStatus === 'Failed') findings.push({ tone: 'bad', text: `The last deploy failed on ${i.id}.` });
        if (i.deploymentStatus === 'Deploying') findings.push({ tone: 'info', text: `A deploy is running on ${i.id}.` });
        if ((i.cpuPercent ?? 0) >= CPU_HIGH)
            findings.push({ tone: 'warn', text: `CPU is at ${Math.round(i.cpuPercent!)}% on ${i.id}.` });
        if ((i.status5xxPerMinute ?? 0) > 0)
            findings.push({
                tone: 'warn',
                text: `${i.id} is answering about ${Math.round(i.status5xxPerMinute!)} errors (5xx) a minute.`,
            });
    }

    // Repeated dips: EB recovers on its own, so these never page anyone but often have one cause (a scheduled job).
    const dips = events.filter((e) => UNHEALTHY.test(e.message)).map((e) => e.at);
    if (dips.length >= 2) {
        const minutes = new Set(dips.map((d) => d.getMinutes()));
        const hourly =
            minutes.size === 1
                ? ` Always at :${String(dips[0].getMinutes()).padStart(2, '0')} past the hour, which looks like something scheduled.`
                : '';
        findings.push({
            tone: 'warn',
            text: `Became unhealthy ${dips.length} times in the last day (${dips.slice(0, 6).map(clock).join(', ')}${dips.length > 6 ? ', …' : ''}).${hourly}`,
        });
    }

    const deploy = events.find((e) => e.source === 'deploy');
    if (deploy && now - deploy.at.getTime() < 60 * 60_000)
        findings.push({ tone: 'info', text: `Deployed ${Math.round((now - deploy.at.getTime()) / 60_000)} minutes ago.` });

    return findings;
}
